import moment from "moment";

/**
 * Product expirations reach the storefront inside the promo description rather than
 * in a field of their own: `promo_addl_descr` is vendor-entered free text and holds
 * either an ordinary promo ("Buy 5 Get 1 Free", "(Sale!)") or an expiration
 * ("Expires at 03/2027", "Short Expiration. 12-2025"). Surfaces that badge the two
 * differently have to tell them apart by reading the text, so the rule lives here —
 * used by the shopping-list item card, the cart line item and checkout's review lines.
 *
 * Replace this with the real field once the backend exposes one.
 */
const EXPIRATION_DESCRIPTION_PATTERN = /expir/i;

const EXPIRATION_DATE_PATTERN = /(\d{1,2})[/-](?:\d{1,2}[/-])?(\d{4})/;

/** True when a promo description is really a product expiration. */
export function isExpirationDescription(description: string): boolean {
  return EXPIRATION_DESCRIPTION_PATTERN.test(description);
}

export function formatExpirationLabel(description: string): string {
  const match = EXPIRATION_DATE_PATTERN.exec(description);
  if (!match) {
    return description;
  }

  const [, month, year] = match;
  const monthIndex = Number(month) - 1;
  // Show vendor's own wording in the case of an invalid date
  if (monthIndex < 0 || monthIndex > 11) {
    return description;
  }

  return `Expires ${moment({ year: Number(year), month: monthIndex }).format("MMM YYYY")}`;
}

/** Partitions promo descriptions so each group can get its own badge treatment. */
export function splitPromoDescriptions(descriptions: string[]): SplitPromoDescriptions {
  return {
    expirationDescriptions: descriptions.filter(isExpirationDescription),
    otherPromoDescriptions: descriptions.filter((description) => !isExpirationDescription(description)),
  };
}

export type SplitPromoDescriptions = {
  expirationDescriptions: string[];
  otherPromoDescriptions: string[];
};
